import ReactGA from 'react-ga4';
import { useState } from "react";
import { ReactComponent as SuccessIcon } from "../../../assets/icons/success.svg";
import { ClientQuizFormType } from "./ClientQuiz";
import { HowDidYouHearAboutUs } from "../patient.types";
import { Link } from "react-router-dom";
import { ReactComponent as FilledCheckboxIcon } from "../../../assets/icons/checkbox-filled.svg";
import { ReactComponent as MapLocationImg } from "../../../assets/icons/map-location.svg";
import { ReactComponent as AlertIcon } from "../../../assets/icons/Alert.svg";
import { ReactComponent as BrokenHeartIcon } from "../../../assets/icons/broken-heart.svg";
import { validateEmail, validatePhone } from "../../../services/utils";
import { api } from "../../../services/api";
import { TextInput } from '../../../components/TextInput';
import { SelectInput } from '../../../components/SelectInput';
import { Button } from '../../../components/Button';
import { WEBSITE_URL } from '../../../env';

export type WaitListForm = {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  enquiryType?: 'client' | 'careProvider' | 'other';
  careProviderType: string;
  terms: boolean;
  zipCode?: string;
}

export function OutOfStateWaitlist({ clientQuizForm }: { clientQuizForm: ClientQuizFormType; }) {
  const [error, setError] = useState<string>("");
  const [errors, setErrors] = useState({} as any);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState<WaitListForm>({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    enquiryType: undefined,
    careProviderType: "",
    terms: false,
    zipCode: clientQuizForm.zipCode,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      // allow only number
      const regex = /^[0-9\b]+$/;
      if (value === "" || regex.test(value)) {
        if (value && !validatePhone(value.toString())) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Invalid mobile number",
          }));
        } else {
          setErrors((prev) => ({ ...prev, mobile: "" }));
        }
        setForm((prev) => ({ ...prev, [name]: value }));
        return;
      } else {
        return;
      }
    }

    if (name === "email") {
      if (value && !validateEmail(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Invalid email format",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {

      const response = await api.waitlistSignup(form)
      if ([200, 201].includes(response.status)) {
        setSuccess(true);

        if (process.env.REACT_APP_ENV === "prod") {
          ReactGA.event("wait-listed-from-quiz")
        }
      }

    } catch (err: any) {
      setError(err?.response?.data?.message || "Something went wrong")
    }
  }

  const disabled = !form.terms || !form.firstName.trim() || !form.lastName.trim() || !form.email || !form.mobile
    || !validateEmail(form.email) || !validatePhone(form.mobile) || !form.enquiryType || (form.enquiryType === 'careProvider' && !form.careProviderType)

  return (
    <>
      <div className="bg-white p-8 rounded-[16px]">
        <div className="grid grid-cols-2">

          <div className="col-span-2 lg:col-span-1 lg:mt-1">
            {/* <SuccessIcon className="w-64 h-auto max-h-96 mx-auto my-4" /> */}
            <BrokenHeartIcon className="w-24 sm:w-36 lg:w-36 h-auto max-h-48 my-4 mx-auto" />
            <h1 className="text-2xl font-bold mt-8 mb-2">We're not in your state yet...</h1>
            <p className="text-left mb-4 mr-10">
            But we'll be there soon! Please fill out the form if you'd like us to keep you updated.
            </p>
          </div>
          <div className="col-span-2 lg:col-span-1 mb-3">

            {success ? <div className='lg:mt-[100px] lg:h-[400px]'>
              <SuccessIcon className="w-24 h-auto max-h-24 mx-auto my-4" />
              <h1 className="text-2xl font-bold mb-2">Thank You!</h1>
              <p className="text-left mb-4 pr-1">
                We will let you know as soon as we are available in your state.
              </p>
              <Link to={WEBSITE_URL}>
                <Button
                  variant="primary"
                  className="!rounded-full w-full text-xl mt-[50px] lg:mt-[200px]"
                >
                  Return to Homepage
                </Button>
              </Link>
            </div> :
              <form className="mt-4 flex flex-col gap-[22px]" onSubmit={handleSubmit}>
                <div className="flex flex-col md:flex-row items-center gap-3">
                  <div className="flex-1 w-full flex flex-col gap-1.5">
                    <label className="text-sm text-black/80">
                      First Name
                    </label>
                    <TextInput
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Enter your first name"
                      maxLength={128}
                    />
                    <div style={{ height: "10px" }} />
                  </div>
                  <div className="flex-1 w-full flex flex-col gap-1.5">
                    <label htmlFor="password" className="text-sm text-black/80">
                      Last Name
                    </label>
                    <TextInput
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Enter your last name"
                      maxLength={128}
                    />
                    <div style={{ height: "10px" }} />
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-3 mt-[-10px]">
                  <div className="flex-1 w-full flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm text-black/80">
                      Email
                    </label>
                    <TextInput
                      type="email"
                      placeholder="Enter your email address"
                      value={form.email}
                      onChange={handleChange}
                      name="email"
                      maxLength={128}
                    />
                    {errors.email && (
                      <span className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1" style={{ height: "10px" }} >
                        {errors.email}
                      </span>
                    )}
                    {!errors.email && <div style={{ height: "18px" }} />}
                  </div>

                  <div className="flex-1 w-full flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm text-black/80">
                      Phone Number
                    </label>
                    <TextInput
                      type="tel"
                      placeholder="Enter your phone number"
                      value={form.mobile}
                      onChange={handleChange}
                      name="mobile"
                      maxLength={10}
                    />
                    {errors.mobile && (
                      <span className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1" style={{ height: "10px" }} >
                        {errors.mobile}
                      </span>
                    )}
                    {!errors.mobile && <div style={{ height: "18px" }} />}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-black/80 mb-[-15px]">
                    Which of the following best describes you?
                  </label>
                  <SelectInput
                    data={[{
                      value: "client",
                      label: "I'm looking to receive therapy for myself or a family member",
                    }, {
                      value: "careProvider",
                      label: "I'm a care provider looking to partner with Headstart",
                    }, {
                      value: "other",
                      label: "Other",
                    }]}
                    name="type"
                    value={form.enquiryType}
                    disabledOptionText={"Which of the following best describes you?"}
                    onChange={(e) => {
                      setForm({ ...form, enquiryType: e.target.value as 'client' | 'careProvider' | 'other' | undefined });
                    }}
                    className={`mt-0`}
                  />
                  {errors.enquiryType && (
                    <div className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1">
                      {errors.enquiryType}
                    </div>
                  )}
                </div>

                {form.enquiryType === 'careProvider' &&
                  <div>
                    <label className="text-sm text-black/80 mb-[-15px]">
                      Which of the following best describes your role as a care provider?
                    </label>
                    <SelectInput
                      data={[{
                        value: "BCBA",
                        label: "BCBA",
                      }, {
                        value: "BCaBA",
                        label: "BCaBA",
                      }, {
                        value: "RBT",
                        label: "RBT",
                      },
                      {
                        value: "Speech Therapist / Occupational Therapist / Speech Language Pathologist",
                        label: "Speech Therapist / Occupational Therapist / Speech Language Pathologist",
                      }, {
                        value: "Other",
                        label: "Other",
                      }]}
                      name="type"
                      value={form.careProviderType}
                      disabledOptionText={"Role as a care provider?"}
                      onChange={(e) => {
                        setForm({ ...form, careProviderType: e.target.value as HowDidYouHearAboutUs });
                      }}
                      className={`mt-0`}
                    />
                    {errors.careProviderType && (
                      <div className="text-red-500 text-xs font-medium mt-2 flex items-center gap-1">
                        {errors.careProviderType}
                      </div>
                    )}
                  </div>
                }

                <div className="flex items-center gap-1.5 mt-3">
                  <div className="flex">
                    <input
                      type="checkbox"
                      id="terms"
                      name="terms"
                      checked={form.terms}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          terms: e.target.checked,
                        }))
                      }
                      className={`w-[22px] h-[22px] cursor-pointer ${form.terms ? "opacity-[0] !h-0 !w-0" : ""
                        }`}
                    />
                    {form.terms ? (
                      <button
                        onClick={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            terms: false,
                          }))
                        }
                      >
                        <FilledCheckboxIcon className="w-[22px] h-[22px]" />
                      </button>
                    ) : null}
                  </div>
                  <label htmlFor="terms" className="text-base text-[#1D1E1B]">
                    I consent to Headstart's{" "}
                    <a
                      href={`${WEBSITE_URL}/privacy-policy`}
                      className="text-primary font-medium hover:text-black"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <div className="mt-3">
                  {error && (
                    <div className="text-red-500 text-xs font-medium mb-4 flex items-center gap-1">
                      <AlertIcon /> {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    className="!rounded-full w-full text-xl"
                    disabled={disabled || saving}
                    loading={saving}
                  >
                    Notify When Available
                  </Button>
                </div>
              </form>}
          </div>

        </div>
      </div>
    </>
  );
}
